/**
 * 退款审核（管理员）。
 *
 * 待处理退款列表 + 通过 / 拒绝 / 完成 操作。
 *
 * @module pages/admin/RefundAdminPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import PageHeader from '@/components/common/PageHeader';
import DataTable, { type Column } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import MoneyText from '@/components/common/MoneyText';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import {
  approveRefund,
  completeRefund,
  listPendingRefunds,
  rejectRefund,
} from '@/services/admin';
import type { RefundRequest, RefundStatus } from '@/types';
import dayjs from 'dayjs';

/** 退款状态元信息。 */
const REFUND_STATUS_META: Record<
  RefundStatus,
  { label: string; color: 'warning' | 'info' | 'error' | 'success' }
> = {
  pending: { label: '待处理', color: 'warning' },
  approved: { label: '已通过', color: 'info' },
  rejected: { label: '已拒绝', color: 'error' },
  completed: { label: '已完成', color: 'success' },
};

/** 待处理操作类型。 */
type PendingAction = 'approve' | 'reject' | 'complete';

/**
 * 退款审核页组件。
 *
 * @returns 页面元素。
 */
export default function RefundAdminPage(): JSX.Element {
  const { data, loading, refetch } = useApi(() => listPendingRefunds());
  const { show, Toast } = useToast();

  const [pending, setPending] = useState<{
    action: PendingAction;
    id: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.action === 'approve') {
        await approveRefund(pending.id);
        show('已通过退款');
      } else if (pending.action === 'reject') {
        await rejectRefund(pending.id);
        show('已拒绝退款');
      } else {
        await completeRefund(pending.id);
        show('退款已完成');
      }
      setPending(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<RefundRequest>[] = [
    { key: 'id', header: 'ID', render: (r) => `#${r.id}` },
    { key: 'userId', header: '用户', render: (r) => `#${r.userId}` },
    { key: 'amount', header: '金额', align: 'right', render: (r) => <MoneyText value={r.amount} /> },
    { key: 'reason', header: '原因' },
    {
      key: 'status',
      header: '状态',
      render: (r) => (
        <StatusChip
          label={REFUND_STATUS_META[r.status].label}
          color={REFUND_STATUS_META[r.status].color}
        />
      ),
    },
    {
      key: 'createdAt',
      header: '申请时间',
      render: (r) => dayjs(r.createdAt).format('YYYY-MM-DD HH:mm'),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (r) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Button size="small" color="success" startIcon={<CheckCircleIcon />} onClick={() => setPending({ action: 'approve', id: r.id })}>
            通过
          </Button>
          <Button size="small" color="info" startIcon={<TaskAltIcon />} onClick={() => setPending({ action: 'complete', id: r.id })}>
            完成
          </Button>
          <Button size="small" color="error" startIcon={<CancelIcon />} onClick={() => setPending({ action: 'reject', id: r.id })}>
            拒绝
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="退款审核" subtitle="处理用户的退款申请" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        仅展示待处理与流程中的退款申请；已结束的申请请通过后端查询。
      </Typography>

      <DataTable
        columns={columns}
        rows={data ?? []}
        rowKey={(r) => r.id}
        loading={loading}
        emptyText="暂无待处理退款"
      />

      <ConfirmDialog
        open={pending != null}
        danger={pending?.action === 'reject'}
        title={
          pending?.action === 'approve'
            ? '通过退款'
            : pending?.action === 'reject'
              ? '拒绝退款'
              : '完成退款'
        }
        message="确定执行该操作？"
        confirmText="确定"
        loading={busy}
        onConfirm={handleConfirm}
        onCancel={() => !busy && setPending(null)}
      />
      {Toast}
    </Box>
  );
}
